import React, { useState, useEffect } from 'react';
import { navigate } from 'gatsby';
import PropTypes from 'prop-types';
import { getUtmValuesFromCookie } from '../../../utils/thirdPartyUtils';
import './marketo.scss';

const personalDemoThreshold = 20;

function fillUtmParams() {
  const utmValues = getUtmValuesFromCookie();
  const marketoForm = window.MktoForms2.allForms()[0];
  marketoForm.addHiddenFields({
    ftutmsource: `${utmValues.__ft_utm_source}`,
    ftutmmedium: `${utmValues.__ft_utm_medium}`,
    ftutmcampaign: `${utmValues.__ft_utm_campaign}`,
    ftutmterm: `${utmValues.__ft_utm_term}`,
    ftutmcontent: `${utmValues.__ft_utm_content}`,
    ltutmsource: `${utmValues.__lt_utm_source}`,
    ltutmmedium: `${utmValues.__lt_utm_medium}`,
    ltutmcampaign: `${utmValues.__lt_utm_campaign}`,
    ltutmterm: `${utmValues.__lt_utm_term}`,
    ltutmcontent: `${utmValues.__lt_utm_content}`,
  });
}

// function NeverBounceScript() {
//   if (!(document.querySelector('#nbScript'))) {
//     const script = document.createElement('script');
//     script.src = 'https://cdn.neverbounce.com/widget/dist/NeverBounce.js';
//     script.id = "nbScript"

//     window._NBSettings = {
//       apiKey: 'public_310bf86df5c629f9b7072e5cc6c926e7',
//       blockFreemail: false,
//       blockRoleAccount: true,
//       blockThrottledAttempts: true,
//       displayPoweredBy: false,
//       feedback: true,
//       feedbackClass: 'nb-feedback',
//       inputLatency: 999,
//       rejectedMessage: 'invalid email',
//       softRejectMessage: 'enter an email',
//       freemailRejectMessage: 'enter a business email',
//       throttleRejectMessage: 'too many attempts, try again later',
//       timeout: 60,
//     };
//     document.querySelector('body').appendChild(script);
//   }
// }

function appendScript(baseUrl, setScriptLoaded) {
  if (window.MktoForms2) return setScriptLoaded(true);

  const script = document.createElement('script');
  script.src = `${baseUrl}/js/forms2/js/forms2.min.js`;
  script.onload = () => (window.MktoForms2 ? setScriptLoaded(true) : null);
  document.body.appendChild(script);
}

function useMarketo({ baseUrl, munchkinId, formId, enableClearbit, redirectUrl }) {
  const [scriptLoaded, setScriptLoaded] = useState(false);
  const [formLoaded, setFormLoaded] = useState(false);

  useEffect(() => {
    if (scriptLoaded && !formLoaded) {
      window.MktoForms2.loadForm(baseUrl, munchkinId, formId, (form) => {
        // if (process.env.NODE_ENV !== 'development') {
        //   NeverBounceScript();
        // }
        form.onSubmit(() => {
          const mapleSyrup = form.getFormElem().find('#maplesyrup');
          if (mapleSyrup.length > 0) {
            const mapleSyrupContent = mapleSyrup.val();
            if (mapleSyrupContent.length > 0) {
              form.submittable(false);
            } else {
              form.submittable(true);
            }
          }
        });
        form.onSuccess((values, followUpUrl) => {
          setFormLoaded(true);
          const toUrl = redirectUrl || followUpUrl;
          const currentPath = document.location.pathname;

          if ((currentPath !== "/subscribe" && currentPath !== "/subscribe/") && formId === "2272") {
            updateFooterSubscribeForm();
          } else {
            postSubmitRedirectUsingClearbit(form, toUrl, enableClearbit);
          }
          return false;
        });
      });
      window.MktoForms2.onFormRender((form) => {
        if (enableClearbit) {
          updatePlaceHolder(form.getFormElem());
        }
        addClassToCheckboxSelectContainer(form.getFormElem());
      });
      window.MktoForms2.whenRendered((form) => destyleMktoForm(form));
    }
    if (formLoaded) {
      fillUtmParams();
    }
    appendScript(baseUrl, setScriptLoaded);
  }, [scriptLoaded, baseUrl, munchkinId, formId, formLoaded]);
}

/**
 * Set placeholder values from labels
 * @param {object} form instance of marketo form
 */
const updatePlaceHolder = (form) => {
  const inputs = form.find('input');
  const textareas = form.find('textarea');

  inputs.each((i) => {
    const input = inputs[i];
    const label = input.parentNode.getElementsByTagName('label');
    const hasPlaceholder = input.attributes.placeholder;
    if (label.length > 0 && !hasPlaceholder) {
      input.setAttribute('placeholder', label[0].textContent);
    }
  });

  textareas.each((i) => {
    const textarea = textareas[i];
    const label = textarea.parentNode.getElementsByTagName('label');
    const hasPlaceholder = textarea.attributes.placeholder;
    if (label.length > 0 && !hasPlaceholder) {
      textarea.setAttribute('placeholder', label[0].textContent);
    }
  });
};

/**
 * Set checkbox-container class to div.mktoFieldWrap to allow custom styling
 * Set select-container class to div.mktoFieldWrap to allow custom styling
 * @param {object} form instance of marketo form
 */
const addClassToCheckboxSelectContainer = (form) => {
  const checkboxes = form.find('input[type=checkbox]');
  const selects = form.find('select');

  checkboxes.each((i) => {
    const checkbox = checkboxes[i];
    checkbox.parentElement.parentElement.classList.add('checkbox-container');
  });

  selects.each((i) => {
    const select = selects[i];
    select.parentElement.classList.add('selectWrap');

    const customSelect = document.createElement('div');
    customSelect.className = 'customSelect';
    select.parentNode.insertBefore(customSelect, select);
    customSelect.appendChild(select);
  });
};

function loadClearbit() {
  const pollForDefinition = function (scope, varname, callback) {
    if (typeof scope[varname] !== 'undefined') {
      return callback();
    }
    const interval = setInterval(function () {
      if (typeof scope[varname] !== 'undefined') {
        clearInterval(interval);
        callback();
      }
    }, 250);
  };
  const script = document.createElement('script');
  script.src = 'https://marketo.clearbit.com/assets/v1/marketo/forms.js';
  script.async = true;
  script.setAttribute('data-clearbit-publishable-key', 'pk_b49bff58911072e9fc652774c9c55b7a');
  script.onerror = function (e) {
    console.log('Clearbit Form JS unable to load');
    pollForDefinition(window, 'MktoForms2', function () {
      window.MktoForms2.whenReady(function (form) {
        form.setValues({ clearbitFormStatus: 'Clearbit Form JS unable to load' });
      });
    });
  };
  document.querySelector('head').appendChild(script);
}

function postSubmitRedirectUsingClearbit(form, followUpUrl, enableClearbit) {
  if (enableClearbit) {
    // eslint-disable-next-line radix
    if (parseInt(document.querySelector('input[name="NumberOfEmployees"]').value) > personalDemoThreshold) {
      // Redirect to Thank you Personal Demo page
      navigate('/thank-you-pd');
    } else {
      // Redirect to Thank you Recorded Demo page
      navigate('/thank-you-rd');
    }
  } else {
    navigate(followUpUrl);
  }
}

/*
 * @author Sanford Whiteman
 * @version v1.104
 * @license MIT License: This license must appear with all reproductions of this software.
 *
 * Create a completely barebones, user-styles-only Marketo form
 * by removing inline STYLE attributes and disabling STYLE and LINK elements
 */

function destyleMktoForm(mktoForm) {
  const formEl = mktoForm.getFormElem()[0];

  // remove element styles from <form> and children
  const styledEls = Array.from(formEl.querySelectorAll('[style]'));
  styledEls.forEach((el) => el.removeAttribute('style'));

  // disable remote stylesheets and local <style>s
  const styleSheets = Array.from(document.styleSheets);
  styleSheets.forEach((ss) => {
    // eslint-disable-next-line no-undef
    if ([mktoForms2BaseStyle, mktoForms2ThemeStyle].indexOf(ss.ownerNode) !== -1 || formEl.contains(ss.ownerNode)) {
      ss.disabled = true;
    }
  });
}

const updateFooterSubscribeForm = () => {
  const successText = "Welcome to the club! Keep an eye for your inbox for the latest from LaunchDarkly";

  document.querySelector('.newsletter-footer-text').innerText = successText;
  document.querySelector('.newsletter-footer-mkto-form').style.display = "none";
}

/**
 * Component for a Marketo form
 * @param {string} formId Marketo form id
 * @param {boolean} [enableClearbit] enables Clearbit enrichment
 * @param {string} [redirectUrl] overrides redirect set in Marketo
 * @example <MarketoForm formId="2034" enableClearbit redirectUrl="http://google.com" />
 */

const MarketoForm = ({ formId, enableClearbit, redirectUrl }) => {
  const baseUrl = '//app-sj31.marketo.com';
  const munchkinId = '850-KKH-319';

  useMarketo({ baseUrl, munchkinId, formId, enableClearbit, redirectUrl });

  useEffect(() => {
    if (enableClearbit) {
      loadClearbit();
    }
  }, []);

  return (
    <div className="mktoFormWrapper">
      <form id={`mktoForm_${formId}`} />
    </div>
  );
};

MarketoForm.propTypes = {
  formId: PropTypes.string.isRequired,
  // eslint-disable-next-line react/require-default-props
  enableClearbit: PropTypes.bool,
};

export default MarketoForm;
